import React, { useState, useEffect, useContext, useCallback, useRef } from 'react'
import styles from './FlashcardView.module.scss'
import { useQuery, useMutation } from '@apollo/client'
import { FLASHCARD_SENTENCES_QUERY, ADD_USER_SENTENCE_MUTATION, RECORD_PRACTICE_MUTATION, SET_WORD_IMAGE_MUTATION, REMOVE_WORD_IMAGE_MUTATION } from '../query/gqlQueries'
import { streamOllamaGenerate } from '../ollama'
import { uploadImageToStore, deleteImageFromStore } from '../imageStore'
import { mapFlashcardSentencesDataOneToMany } from '../query/queryMappers'
import UserContext from '../context/UserContext'
import useLearnerContext from '../hooks/useLearnerContext'
import languages from '../languages'
import { speak } from '../speech/speak'
import voices from '../speech/voices'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import CloseIcon from '@mui/icons-material/Close'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import RefreshIcon from '@mui/icons-material/Refresh'
import LightModeIcon from '@mui/icons-material/LightMode'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import { romanize as romanizeKorean } from '@romanize/korean'
import HandwritingView from './HandwritingView'
import ReadingFlowView from './ReadingFlowView'
import RecallFlowView from './RecallFlowView'
import RecallPlusView from './RecallPlusView'
import VocabChatView from './VocabChatView'
import { celebrate, popScale, shake, enterUp } from './juice'

// Korean palatalization: a syllable ending in ㅌ/ㄷ batchim followed by 이 is
// pronounced as 치/지 (e.g. 같이 → 가치 [gachi], 굳이 → 구지 [guji]).
// @romanize/korean handles ㄷ-palatalization but misses ㅌ-palatalization, so
// we rewrite the hangul up-front. Applied to both cases for safety — the ㄷ
// case is a no-op when the downstream library already gets it right.
const HANGUL_BASE = 0xAC00
const HANGUL_END = HANGUL_BASE + 11172
const I_SYLLABLE = 0xC774 // 이
const CHI_SYLLABLE = 0xCE58 // 치
const JI_SYLLABLE = 0xC9C0 // 지
const FINAL_T_BATCHIM = 25 // ㅌ
const FINAL_D_BATCHIM = 7  // ㄷ
const palatalizeForRomanization = (text) => {
  const out = []
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i)
    const next = text.charCodeAt(i + 1)
    if (code >= HANGUL_BASE && code < HANGUL_END && next === I_SYLLABLE) {
      const final = (code - HANGUL_BASE) % 28
      if (final === FINAL_T_BATCHIM) {
        out.push(String.fromCharCode(code - FINAL_T_BATCHIM), String.fromCharCode(CHI_SYLLABLE))
        i++
        continue
      }
      if (final === FINAL_D_BATCHIM) {
        out.push(String.fromCharCode(code - FINAL_D_BATCHIM), String.fromCharCode(JI_SYLLABLE))
        i++
        continue
      }
    }
    out.push(text[i])
  }
  return out.join('')
}

// A letter outside the Latin script — the only case where a romanization says
// anything the text doesn't. English, Turkish or Portuguese text IS its own
// romanization, so asking the model for one just echoes the card.
const hasNonLatinLetter = (s) => /(?!\p{Script=Latin})\p{L}/u.test(s || '')

const Romanization = ({ text, lang }) => {
  const [romanization, setRomanization] = useState(null)
  const [loading, setLoading] = useState(false)
  const lastKey = useRef(null)
  // Never romanize the learner's own language(s) from the profile: an English
  // speaker doesn't need "to boil" spelled out again as "to boil".
  const { username } = useContext(UserContext)
  const { native } = useLearnerContext(username)
  const isNative = (native || []).some(l => l && l.code === lang)
  const wanted = !!text && !isNative && hasNonLatinLetter(text)

  useEffect(() => {
    if (!wanted) return
    const key = `${lang || ''}::${text || ''}`
    if (lastKey.current === key) return
    lastKey.current = key

    // Korean: use a deterministic, rules-based romanizer (linking, ㅎ-elision,
    // double-batchim resyllabification). We pre-apply palatalization at the
    // hangul level to cover the ㅌ-batchim + 이 case the library misses.
    // Synchronous, no network round-trip, no LLM hallucination.
    if (lang === 'kor') {
      try {
        setRomanization(romanizeKorean(palatalizeForRomanization(text)) || '')
      } catch (err) {
        setRomanization('')
      }
      setLoading(false)
      return
    }

    const fetchRomanization = async () => {
      setLoading(true)
      try {
        const serverUrl = process.env.REACT_APP_OLLAMA_URI
        const apiKey = process.env.REACT_APP_OLLAMA_KEY
        const langName = (lang && languages[lang]) || 'the source language'
        const prompt = `Romanize the following ${langName} text the way it is actually PRONOUNCED when spoken aloud — not a character-by-character transliteration.

Use the standard pronunciation-based romanization system for the language:
- Japanese → Hepburn romaji (e.g. こんにちは → konnichiwa)
- Mandarin Chinese → Hanyu Pinyin WITH tone marks (e.g. 你好 → nǐ hǎo)
- Cantonese → Jyutping with tones
- Korean → Revised Romanization (e.g. 안녕하세요 → annyeonghaseyo)
- Hindi / Marathi / other Devanagari → simple phonetic Hunterian-style (e.g. नमस्ते → namaste, क्षत्रिय → kshatriya)
- Sanskrit → IAST
- Arabic / Persian / Urdu → phonetic transcription as actually spoken (use sh, kh, gh, etc.)
- Hebrew → phonetic (e.g. שלום → shalom)
- Russian / Ukrainian / other Cyrillic → BGN/PCGN phonetic (e.g. привет → privet)
- Greek → phonetic (e.g. καλημέρα → kalimera)
- Thai → Royal Thai General System (RTGS)
- Lao / Khmer / Burmese → standard phonetic romanization
- Tibetan → THL Simplified Phonetic

Focus on how it SOUNDS, not how the symbols map. If a script letter is silent or merges into a different sound in actual speech, reflect that in the output.

Output ONLY the romanized form on a single line. No quotes, no original script, no translation, no explanation, no labels like "Romanization:", no preamble.

Text: ${text}`
        const res = await fetch(`${serverUrl}/api/generate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': apiKey
          },
          body: JSON.stringify({
            model: 'qwen2.5:14b',
            prompt,
            stream: false,
            options: { temperature: 0.1, num_predict: 150 }
          })
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data = await res.json()
        let cleaned = (data.response || '').trim()
        // Strip common preambles the model sometimes adds despite instructions
        cleaned = cleaned.replace(/^(romanization|romanisation|romaji|pinyin|transliteration|pronunciation)\s*[:：-]\s*/i, '').trim()
        // Strip surrounding quotes/backticks
        cleaned = cleaned.replace(/^["'`“”‘’]+|["'`“”‘’]+$/g, '').trim()
        // If the model returned multiple lines, keep the first non-empty one
        const firstLine = cleaned.split(/\r?\n/).map(s => s.trim()).find(Boolean) || ''
        setRomanization(firstLine)
      } catch (err) {
        setRomanization('')
      }
      setLoading(false)
    }
    fetchRomanization()
  }, [text, lang, wanted])

  if (!wanted) return null
  if (loading) return <div className={styles.romanization}>...</div>
  // Nothing to add if the model just echoed the text back.
  if (!romanization || romanization.trim().toLowerCase() === String(text).trim().toLowerCase()) return null

  return <div className={styles.romanization}>{romanization}</div>
}

// ─── Mnemonic image ─────────────────────────────────────────────────────────
// A small persistent image attached to a vocabulary word that acts as a memory
// hook (e.g. 끓다 "boiling" → image of someone blowing on a pot to cool it).
//
// Generation pipeline (all local, no Claude/OpenAI):
//   1. Optional user hint ("sounds like cool-ta") is captured inline.
//   2. We ask Ollama (qwen2.5:14b) to turn {word, meaning, hint?} into a single
//      vivid Stable Diffusion prompt — Ollama is much better than SD at the
//      "visual pun" inference; SD is much better at the rendering.
//   3. We POST the resulting prompt to the existing SD endpoint (the same one
//      the Image Quiz mode uses today).
//   4. SET_WORD_IMAGE_MUTATION persists the base64 result on the backend, which
//      writes the PNG to disk and stores the URL on the matching Word node
//      (per-user dedup by text+lang+user).
const MnemonicImage = ({ text, lang, meaning, imageUrl, imageDescription, username, onSaved }) => {
  const [setWordImage] = useMutation(SET_WORD_IMAGE_MUTATION)
  const [removeWordImage] = useMutation(REMOVE_WORD_IMAGE_MUTATION)
  const [hint, setHint] = useState('')
  const [hintOpen, setHintOpen] = useState(false)
  const [status, setStatus] = useState('idle') // idle | prompting | drawing | saving | error
  const [errorMsg, setErrorMsg] = useState(null)
  const [progressMsg, setProgressMsg] = useState('')

  // Reset transient UI when the card we're showing changes.
  useEffect(() => {
    setHint(imageDescription || '')
    setHintOpen(false)
    setStatus('idle')
    setErrorMsg(null)
    setProgressMsg('')
  }, [text, lang, imageUrl, imageDescription])

  const generate = useCallback(async () => {
    if (!text || !lang || !username) {
      setErrorMsg('Missing word context — cannot generate.')
      setStatus('error')
      return
    }
    setStatus('prompting')
    setErrorMsg(null)
    setProgressMsg('Asking Ollama for a memory hook…')

    const ollamaUrl = process.env.REACT_APP_OLLAMA_URI || 'http://localhost:11434'
    const sdUrl = process.env.REACT_APP_IMAGE_GEN_URI
    const sdKey = process.env.REACT_APP_IMAGE_GEN_KEY
    if (!sdUrl) {
      setErrorMsg('REACT_APP_IMAGE_GEN_URI not configured.')
      setStatus('error')
      return
    }

    const langName = languages[lang] || lang
    const promptOllama = `You are designing a single-scene image that helps an English speaker remember a ${langName} vocabulary word using a visual memory hook.

Word: ${text}
Meaning: ${meaning || '(unknown)'}
${hint ? `Memory hook from the user: ${hint}` : ''}

Output a single vivid Stable Diffusion prompt (under 30 words) that visually combines:
• a literal depiction of the meaning, AND
• a visual pun based on how the word sounds when spoken aloud to an English speaker.

Output ONLY the prompt sentence. No quotes, no preamble, no labels, no markdown.`

    let sdPrompt = ''
    try {
      sdPrompt = await streamOllamaGenerate({
        serverUrl: ollamaUrl,
        apiKey: process.env.REACT_APP_OLLAMA_KEY,
        body: {
          model: 'qwen2.5:14b',
          prompt: promptOllama,
          options: { temperature: 0.6, num_predict: 120 }
        },
        onProgress: (acc) => setProgressMsg(`Memory hook: ${acc.slice(-60)}…`)
      })
      sdPrompt = (sdPrompt || '').replace(/^["'`]+|["'`]+$/g, '').trim()
      if (!sdPrompt) throw new Error('Ollama returned empty prompt')
    } catch (e) {
      console.error('Ollama prompt failed', e)
      setErrorMsg('Ollama prompt generation failed — falling back to plain prompt.')
      sdPrompt = `minimal flat illustration of ${text} (${meaning || ''}), single centered subject, clean vector-style, no text`
    }

    setStatus('drawing')
    setProgressMsg(`Drawing: ${sdPrompt.slice(0, 80)}…`)
    let base64
    try {
      const finalPrompt = `${sdPrompt}, minimal flat illustration, single centered subject, clean vector-style artwork, bold solid colors, pastel background, no text, no letters, no words, no typography`
      const res = await fetch(`${sdUrl}/sdapi/v1/txt2img`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': sdKey || '' },
        body: JSON.stringify({
          prompt: finalPrompt,
          negative_prompt: 'text, letters, words, typography, font, caption, label, logo, watermark, signature, numbers, symbols, signage, cluttered background, photorealistic, blurry, low quality, nsfw',
          steps: 8,        // a bit higher than Image Quiz so mnemonic looks nicer
          width: 512,
          height: 512,
          cfg_scale: 4
        })
      })
      if (!res.ok) throw new Error(`SD HTTP ${res.status}`)
      const data = await res.json()
      if (!data.images?.[0]) throw new Error('SD returned no image')
      base64 = data.images[0]
    } catch (e) {
      console.error('SD failed', e)
      setErrorMsg(e.message || 'Image generation failed.')
      setStatus('error')
      return
    }

    // Upload PNG bytes directly to the image-store (Mac mini), get back a
    // public URL. Render's GraphQL server never sees these bytes — its disk
    // is ephemeral and we don't want mnemonics vanishing on redeploy.
    setStatus('saving')
    setProgressMsg('Uploading to image store…')
    let uploadedUrl
    try {
      uploadedUrl = await uploadImageToStore({
        kind: 'word', text, lang, username, imageBase64: base64
      })
    } catch (e) {
      console.error('image-store upload failed', e)
      setErrorMsg(e.message || 'Image-store upload failed.')
      setStatus('error')
      return
    }

    // Hand the URL to the GraphQL server, which records it on the Word node.
    // Best-effort: capture the URL we're replacing so we can clean up the
    // old file from the image-store after the new one is persisted.
    setProgressMsg('Recording on the graph…')
    const oldUrl = imageUrl
    try {
      const { data } = await setWordImage({
        variables: { text, lang, username, imageUrl: uploadedUrl, description: hint || null }
      })
      const savedUrl = data?.setWordImage?.imageUrl || uploadedUrl
      if (onSaved) onSaved(savedUrl, hint || null)
      setStatus('idle')
      setHintOpen(false)
      // Async cleanup of the previous file — fire and forget; failure is OK.
      if (oldUrl && oldUrl !== savedUrl) {
        deleteImageFromStore(oldUrl)
      }
    } catch (e) {
      console.error('setWordImage failed', e)
      setErrorMsg(e.message || 'Save failed.')
      setStatus('error')
      // The bytes did land in the image-store but we couldn't record them.
      // Best-effort: roll back the upload so we don't leak files.
      deleteImageFromStore(uploadedUrl)
    }
  }, [text, lang, meaning, hint, username, imageUrl, setWordImage, onSaved])

  const handleRemove = useCallback(async (e) => {
    e.stopPropagation()
    if (!text || !lang || !username) return
    if (!window.confirm('Remove this mnemonic image?')) return
    try {
      // Clear it from the graph first — that's the load-bearing step.
      await removeWordImage({ variables: { text, lang, username } })
      // Then best-effort delete the underlying file from the image-store.
      if (imageUrl) deleteImageFromStore(imageUrl)
      if (onSaved) onSaved(null, null)
    } catch (e) {
      console.error('removeWordImage failed', e)
    }
  }, [text, lang, username, imageUrl, removeWordImage, onSaved])

  const isBusy = status === 'prompting' || status === 'drawing' || status === 'saving'

  // ── Has image ──
  if (imageUrl && !isBusy) {
    return (
      <div className={styles.mnemonic} onClick={(e) => e.stopPropagation()}>
        <img src={imageUrl} alt={`mnemonic for ${text}`} className={styles.mnemonicImage} />
        <div className={styles.mnemonicActions}>
          {imageDescription && (
            <span className={styles.mnemonicHintReadout} title="Hint that produced this image">“{imageDescription}”</span>
          )}
          <button className={styles.mnemonicSmallBtn} onClick={(e) => { e.stopPropagation(); setHintOpen(true); generate() }} disabled={isBusy}>
            ↻ Regenerate
          </button>
          <button className={styles.mnemonicSmallBtn} onClick={handleRemove} disabled={isBusy}>
            ✕ Remove
          </button>
        </div>
      </div>
    )
  }

  // ── No image / generating / error ──
  return (
    <div className={styles.mnemonic} onClick={(e) => e.stopPropagation()}>
      {isBusy ? (
        <div className={styles.mnemonicPlaceholder}>
          <div className={styles.mnemonicSpinner} />
          <div className={styles.mnemonicProgress}>{progressMsg || 'Working…'}</div>
        </div>
      ) : (
        <>
          {!hintOpen ? (
            <button
              className={styles.mnemonicGenerateBtn}
              onClick={(e) => { e.stopPropagation(); setHintOpen(true) }}>
              🎨 Generate mnemonic image
            </button>
          ) : (
            <div className={styles.mnemonicHintBlock}>
              <label className={styles.mnemonicHintLabel}>
                Optional hint (e.g. <em>"sounds like cool-ta, blowing on hot water"</em>):
              </label>
              <textarea
                className={styles.mnemonicHintInput}
                value={hint}
                onChange={(e) => setHint(e.target.value)}
                placeholder="Leave empty to let the AI invent a hook from the meaning and sound"
                rows={2}
                autoFocus
              />
              <div className={styles.mnemonicHintActions}>
                <button className={styles.mnemonicSmallBtn} onClick={(e) => { e.stopPropagation(); setHintOpen(false) }}>
                  Cancel
                </button>
                <button className={`${styles.mnemonicSmallBtn} ${styles.mnemonicPrimaryBtn}`} onClick={(e) => { e.stopPropagation(); generate() }}>
                  Generate
                </button>
              </div>
            </div>
          )}
          {errorMsg && <div className={styles.mnemonicError}>{errorMsg}</div>}
        </>
      )}
    </div>
  )
}

// The non-English (target-language) side of a card, whichever orientation it's in.
const foreignOf = (card) => {
  const t = card?.translations?.[0]
  if (card?.sourceLanguage && card.sourceLanguage !== 'eng' && card.source) return { text: card.source, lang: card.sourceLanguage }
  if (t?.target && t.targetLanguage && t.targetLanguage !== 'eng') return { text: t.target, lang: t.targetLanguage }
  return null
}

// Light / dark, shared with the lesson and Special Mode views through the same
// localStorage key. Dark by default; an explicit choice is remembered per device.
const THEME_KEY = 'lessonTheme'
const readTheme = () => { try { const t = localStorage.getItem(THEME_KEY); return t === 'light' || t === 'dark' ? t : 'dark' } catch { return 'dark' } }

export default function FlashcardView({ voiceSpeed, setIsVisible, providedFlashcards, onClose }) {
  const [theme, setTheme] = useState(readTheme)
  const toggleTheme = () => setTheme(t => {
    const next = t === 'dark' ? 'light' : 'dark'
    try { localStorage.setItem(THEME_KEY, next) } catch { /* private mode */ }
    return next
  })
  // Listening drills (Listen Quiz, Audio Match) speak slower than the rest of
  // the app: a dictation-style prompt is easier to catch at about three
  // quarters of the chosen speed.
  const slowSpeed = Math.max(0.5, (voiceSpeed || 1) * 0.75)
  const { username } = useContext(UserContext)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isFlipped, setIsFlipped] = useState(false)
  const [stats, setStats] = useState({ mastered: 0, streak: 0 })
  const [flashcards, setFlashcards] = useState([])

  // Tracks which cards have been visited in the current shuffle cycle.
  // When the set fills up, we clear it so the deck "re-shuffles" naturally.
  const seenRef = useRef(new Set())

  // Juice: pop the streak counter when it climbs, spring each new card in.
  const streakRef = useRef(null)
  const prevStreakRef = useRef(0)
  const sceneRef = useRef(null)
  useEffect(() => {
    if (stats.streak > prevStreakRef.current) popScale(streakRef.current)
    prevStreakRef.current = stats.streak
  }, [stats.streak])
  useEffect(() => {
    enterUp(sceneRef.current)
  }, [currentIndex])

  const [isQuizMode, setIsQuizMode] = useState(false)
  const [quizType, setQuizType] = useState('reading')
  const [quizOptions, setQuizOptions] = useState([])
  const [isQuizLoading, setIsQuizLoading] = useState(false)
  const [quizAnswered, setQuizAnswered] = useState(null)
  const [selectedOption, setSelectedOption] = useState(null)
  const [autoSpeakCorrect, setAutoSpeakCorrect] = useState(true)
  const [showRomanization, setShowRomanization] = useState(true)

  // ── Match game state ────────────────────────────────────────────────────────

  const [isMatchMode, setIsMatchMode] = useState(false)
  const [isAudioMatch, setIsAudioMatch] = useState(false)
  const [leftTiles, setLeftTiles] = useState([])
  const [rightTiles, setRightTiles] = useState([])
  const [selLeft, setSelLeft] = useState(null)
  const [selRight, setSelRight] = useState(null)
  const [matchedIds, setMatchedIds] = useState(new Set())
  const [wrongPair, setWrongPair] = useState(null)
  const [matchStats, setMatchStats] = useState({ correct: 0, attempts: 0 })
  const [matchComplete, setMatchComplete] = useState(false)
  // Audio-match: spline connectors drawn between matched left/right tiles.
  const matchColsRef = useRef(null)
  const leftTileRefs = useRef({})
  const rightTileRefs = useRef({})
  const [matchLinks, setMatchLinks] = useState([])

  // ── Memory game state ───────────────────────────────────────────────────────
  const [isMemoryMode, setIsMemoryMode] = useState(false)
  const [memoryTiles, setMemoryTiles] = useState([])
  const [memoryFlipped, setMemoryFlipped] = useState([]) // tile ids currently face-up (max 2)
  const [memoryMatched, setMemoryMatched] = useState(new Set()) // pairIds matched
  const [memoryStats, setMemoryStats] = useState({ correct: 0, attempts: 0 })
  const [memoryComplete, setMemoryComplete] = useState(false)
  const [memorySpeakForeign, setMemorySpeakForeign] = useState(false) // read target-lang tiles aloud

  // Scramble and Image Quiz were removed (Sep 2026): Scramble quizzed the wrong
  // side of the card, Image Quiz cost an image-model round-trip per card. The
  // Verb Drill moved to Special Mode, where the other grammar drills live.

  // ── Handwriting & flow-reading overlays ─────────────────────────────────────
  const [isWriteMode, setIsWriteMode] = useState(false)
  const [isFlowMode, setIsFlowMode] = useState(false)
  const [isRecallMode, setIsRecallMode] = useState(false)
  const [isRecallPlusMode, setIsRecallPlusMode] = useState(false)
  // NOTE (Sep 2026): Chat probably doesn't belong in Flashcards — it's
  // conversation practice, not a card game — and may get its own entry in the
  // Learn grid. Kept here for now, on purpose.
  const [isChatMode, setIsChatMode] = useState(false)

  // ── Sentence builder state ──────────────────────────────────────────────────
  const [isBuildMode, setIsBuildMode] = useState(false)
  const [buildBank, setBuildBank] = useState([])     // [{id, word, used}]
  const [buildAnswer, setBuildAnswer] = useState([]) // array of bank ids
  const [buildStatus, setBuildStatus] = useState(null)

  const [sourceLang, setSourceLang] = useState('eng')
  const [targetLang, setTargetLang] = useState('spa')
  const [tagInput, setTagInput] = useState('')
  const [tagFilter, setTagFilter] = useState('')
  
  const [isAdding, setIsAdding] = useState(false)
  const [isBulkMode, setIsBulkMode] = useState(false)
  const [newSourceText, setNewSourceText] = useState('')
  const [newTargetText, setNewTargetText] = useState('')
  const [newTag, setNewTag] = useState('')
  const [bulkTsvText, setBulkTsvText] = useState('')

  const { data, loading, error, refetch } = useQuery(FLASHCARD_SENTENCES_QUERY, {
    variables: { 
      username, 
      sourceLang: sourceLang || null, 
      targetLang: targetLang || null, 
      tags: tagFilter ? [tagFilter] : null 
    },
    skip: !username || !!providedFlashcards
  })

  const [addUserSentence] = useMutation(ADD_USER_SENTENCE_MUTATION, {
    onCompleted: () => {
      setIsAdding(false)
      setNewSourceText('')
      setNewTargetText('')
      setBulkTsvText('')
      refetch()
    }
  })

  const [recordPracticeMutation] = useMutation(RECORD_PRACTICE_MUTATION)

  const recordPractice = useCallback((itemId, isCorrect) => {
    if (!itemId || !username) return
    recordPracticeMutation({
      variables: { username, itemId, itemType: 'Sentence', isCorrect }
    }).catch(err => console.error('recordPractice failed:', err))
  }, [recordPracticeMutation, username])

  useEffect(() => {
    if (providedFlashcards) {
      setFlashcards(providedFlashcards)
      seenRef.current = new Set()
      return
    }
    if (data) {
      const mapped = mapFlashcardSentencesDataOneToMany(data)
      // Filter out the "No matching sentences" placeholder
      const validCards = mapped.filter(c => c.sourceId !== null)
      setFlashcards(validCards)
      seenRef.current = new Set()
    }
  }, [data, providedFlashcards])

  useEffect(() => {
    if (isQuizMode && quizType === 'listening' && flashcards[currentIndex]) {
      const card = flashcards[currentIndex]
      if (card.source) {
        speak(card.source, voices[card.sourceLanguage] || voices['eng'], slowSpeed)
      }
    }
  }, [isQuizMode, quizType, currentIndex, flashcards, slowSpeed])

  // Match pair check — lives in useEffect to avoid stale closure on selLeft/selRight
  useEffect(() => {
    if (selLeft === null || selRight === null) return
    if (wrongPair) return
    setMatchStats(prev => ({ ...prev, attempts: prev.attempts + 1 }))
    const leftTile = leftTiles.find(t => t.pairId === selLeft)
    if (selLeft === selRight) {
      setMatchedIds(prev => new Set([...prev, selLeft]))
      setSelLeft(null)
      setSelRight(null)
      setMatchStats(prev => ({ ...prev, correct: prev.correct + 1 }))
      recordPractice(leftTile?.targetId, true)
    } else {
      const l = selLeft, r = selRight
      setWrongPair({ left: l, right: r })
      recordPractice(leftTile?.targetId, false)
      setTimeout(() => { setSelLeft(null); setSelRight(null); setWrongPair(null) }, 700)
    }
  }, [selLeft, selRight]) // intentionally omits wrongPair — read only as an early-exit guard

  useEffect(() => {
    if (leftTiles.length > 0 && matchedIds.size === leftTiles.length) {
      setMatchComplete(true)
      celebrate(window.innerWidth / 2, window.innerHeight / 2.5, { count: 40, spread: 220 })
    }
  }, [matchedIds, leftTiles.length])

  // Recompute the left→right connector coordinates whenever a pair is matched
  // (or the layout changes). Each spline runs from the audio tile's right edge
  // to the sentence tile's left edge, across the gap.
  useEffect(() => {
    if (!isMatchMode || !isAudioMatch) { setMatchLinks([]); return }
    const compute = () => {
      const cont = matchColsRef.current
      if (!cont) return
      const c = cont.getBoundingClientRect()
      const links = []
      matchedIds.forEach(pairId => {
        const l = leftTileRefs.current[pairId], r = rightTileRefs.current[pairId]
        if (!l || !r) return
        const lr = l.getBoundingClientRect(), rr = r.getBoundingClientRect()
        links.push({
          pairId,
          x1: lr.right - c.left, y1: lr.top + lr.height / 2 - c.top,
          x2: rr.left - c.left, y2: rr.top + rr.height / 2 - c.top,
        })
      })
      setMatchLinks(links)
    }
    const raf = requestAnimationFrame(compute)
    const t = setTimeout(compute, 90)
    window.addEventListener('resize', compute)
    return () => { cancelAnimationFrame(raf); clearTimeout(t); window.removeEventListener('resize', compute) }
  }, [isMatchMode, isAudioMatch, matchedIds, leftTiles, rightTiles])

  useEffect(() => {
    const currentCard = flashcards[currentIndex]
    if (!isQuizMode || !currentCard || !currentCard.translations?.[0]?.target) return

    let isMounted = true
    const fetchOptions = async () => {
      setIsQuizLoading(true)
      setQuizAnswered(null)
      setSelectedOption(null)

      const correctTarget = currentCard.translations[0].target
      const cardTargetLang = currentCard.translations[0].targetLanguage || targetLang

      // ── Prefer in-deck distractors so we drill the actual lesson vocab ────
      const inDeckDistractors = flashcards
        .filter((c, i) =>
          i !== currentIndex &&
          c.translations?.[0]?.target &&
          c.translations[0].target !== correctTarget &&
          (c.translations[0].targetLanguage || targetLang) === cardTargetLang
        )
        .map(c => c.translations[0].target)

      // De-dupe (in case duplicate targets exist across cards)
      const uniqueDistractors = Array.from(new Set(inDeckDistractors))

      if (uniqueDistractors.length >= 3) {
        const picked = [...uniqueDistractors].sort(() => Math.random() - 0.5).slice(0, 3)
        const allOptions = [correctTarget, ...picked]
        for (let i = allOptions.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [allOptions[i], allOptions[j]] = [allOptions[j], allOptions[i]];
        }
        if (isMounted) {
          setQuizOptions(allOptions)
          setIsQuizLoading(false)
        }
        return
      }

      // ── Fallback: deck too small to fill 4 options, ask Ollama ───────────
      try {
        const serverUrl = process.env.REACT_APP_OLLAMA_URI
        const apiKey = process.env.REACT_APP_OLLAMA_KEY

        const prompt = `Generate 3 distinct, incorrect but plausible translations or words in the language "${languages[cardTargetLang] || cardTargetLang}" for the concept "${currentCard.source}". Do not include the correct answer "${correctTarget}". Return ONLY a JSON array of 3 strings. Example: ["word1", "word2", "word3"]`

        const res = await fetch(`${serverUrl}/api/generate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': apiKey
          },
          body: JSON.stringify({
            model: 'qwen2.5:14b',
            prompt,
            stream: false,
            options: { temperature: 0.7, num_predict: 100 }
          })
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data = await res.json()
        let responseText = data.response?.trim() || '[]'
        
        responseText = responseText.replace(/```json/g, '').replace(/```/g, '').trim()
        
        let parsed = []
        try {
          parsed = JSON.parse(responseText)
        } catch (e) {
          parsed = responseText.split(/[\r\n]+/).map(s => s.replace(/^[-*\d.\s"']+/, '').replace(/["']+$/, '')).filter(s => s).slice(0, 3)
        }
        
        if (!Array.isArray(parsed)) parsed = ["Error parsing option 1", "Error parsing option 2", "Error parsing option 3"]
        while (parsed.length < 3) parsed.push("Fallback option " + parsed.length)
        
        const allOptions = [correctTarget, ...parsed.slice(0, 3)]
        for (let i = allOptions.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [allOptions[i], allOptions[j]] = [allOptions[j], allOptions[i]];
        }
        
        if (isMounted) {
          setQuizOptions(allOptions)
        }
      } catch (err) {
        console.error(err)
        if (isMounted) setQuizOptions([currentCard.translations[0].target, "Error 1", "Error 2", "Error 3"])
      } finally {
        if (isMounted) setIsQuizLoading(false)
      }
    }
    fetchOptions()
    return () => { isMounted = false }
  }, [isQuizMode, flashcards, currentIndex, targetLang])

  // Pick the next card at random, but never repeat a card until every card in
  // the deck has been seen once (Spotify-style shuffle).
  const pickNextRandomIndex = useCallback(() => {
    if (flashcards.length <= 1) return 0
    seenRef.current.add(currentIndex)
    if (seenRef.current.size >= flashcards.length) {
      // Full cycle done — reset, keeping the current card marked so we don't
      // immediately bounce back to it.
      seenRef.current = new Set([currentIndex])
    }
    const candidates = flashcards
      .map((_, i) => i)
      .filter(i => !seenRef.current.has(i))
    if (candidates.length === 0) {
      // Fallback (e.g. deck size 1): just pick anything but current.
      const others = flashcards.map((_, i) => i).filter(i => i !== currentIndex)
      return others.length ? others[Math.floor(Math.random() * others.length)] : currentIndex
    }
    return candidates[Math.floor(Math.random() * candidates.length)]
  }, [flashcards, currentIndex])

  const handleNext = useCallback(() => {
    setIsFlipped(false)
    setQuizAnswered(null)
    setSelectedOption(null)
    const next = pickNextRandomIndex()
    setTimeout(() => setCurrentIndex(next), 150)
  }, [pickNextRandomIndex])

  // Prev keeps a linear "step backwards" semantic so the user can always
  // bounce back to the card they just left (within the current shuffled order).
  const handlePrev = useCallback(() => {
    setIsFlipped(false)
    setQuizAnswered(null)
    setSelectedOption(null)
    setTimeout(() => {
      setCurrentIndex((prev) => (prev - 1 + flashcards.length) % flashcards.length)
    }, 150)
  }, [flashcards.length])

  // Unified mode launcher — shuffles the deck so traversal is randomised,
  // then flips into whichever game mode was requested.
  const startMode = (mode) => {
    setFlashcards(prev => [...prev].sort(() => Math.random() - 0.5))
    setCurrentIndex(0)
    seenRef.current = new Set()
    setIsFlipped(false)
    setQuizAnswered(null)
    setSelectedOption(null)

    switch (mode) {
      case 'reading':
        setIsQuizMode(true); setQuizType('reading'); break
      case 'listening':
        setIsQuizMode(true); setQuizType('listening'); break
      case 'build':
        setIsBuildMode(true); break
      case 'match':
        setIsAudioMatch(false); startMatchRound(); setIsMatchMode(true); break
      case 'audio-match':
        setIsAudioMatch(true); startMatchRound(); setIsMatchMode(true); break
      case 'memory':
        startMemoryRound(); setIsMemoryMode(true); break
      case 'write':
        setIsWriteMode(true); break
      case 'flow':
        setIsFlowMode(true); break
      case 'recall':
        setIsRecallMode(true); break
      case 'recallplus':
        setIsRecallPlusMode(true); break
      case 'chat':
        setIsChatMode(true); break
      default: break
    }
  }

  const handleQuizAnswer = (option, e) => {
    if (quizAnswered === 'correct') return
    const targetLangCode = flashcards[currentIndex]?.translations?.[0]?.targetLanguage || targetLang
    const correctTarget = flashcards[currentIndex]?.translations?.[0]?.target
    const isCorrect = option === correctTarget
    setSelectedOption(option)
    recordPractice(flashcards[currentIndex]?.translations?.[0]?.targetId, isCorrect)
    if (isCorrect) {
      if (e) celebrate(e.clientX, e.clientY)
      if (autoSpeakCorrect) {
        if (quizType === 'listening' || quizType === 'reading') {
          speak(flashcards[currentIndex].source, voices[flashcards[currentIndex].sourceLanguage] || voices['eng'], quizType === 'listening' ? slowSpeed : voiceSpeed)
        } else {
          speak(option, voices[targetLangCode], voiceSpeed)
        }
      }
      setQuizAnswered('correct')
      setStats(prev => ({ ...prev, streak: prev.streak + 1, mastered: prev.mastered + 1 }))
      setTimeout(() => {
        handleNext()
      }, autoSpeakCorrect ? 2500 : 2000)
    } else {
      if (e) shake(e.currentTarget.parentElement || e.currentTarget)
      setQuizAnswered('incorrect')
      setStats(prev => ({ ...prev, streak: 0 }))
    }
  }

  const handleFlip = () => {
    setIsFlipped(!isFlipped)
    if (!isFlipped) {
      setStats(prev => ({ ...prev, streak: prev.streak + 1, mastered: prev.mastered + 1 }))
    }
  }

  const handleSpeak = (e, text, lang) => {
    e.stopPropagation()
    speak(text, voices[lang], voiceSpeed)
  }

  const startMatchRound = useCallback(() => {
    const validCards = flashcards.filter(c => c.translations?.[0]?.target)
    if (validCards.length === 0) return
    const pool = [...validCards].sort(() => Math.random() - 0.5)
    const picked = pool.slice(0, Math.min(5, pool.length))
    const round = picked.map((card, i) => ({
      pairId: i,
      source: card.source,
      sourceLanguage: card.sourceLanguage,
      target: card.translations[0].target,
      targetLanguage: card.translations[0].targetLanguage,
      targetId: card.translations[0].targetId,
    }))
    setLeftTiles([...round].sort(() => Math.random() - 0.5))
    setRightTiles([...round].sort(() => Math.random() - 0.5))
    setSelLeft(null); setSelRight(null)
    setMatchedIds(new Set())
    setWrongPair(null)
    setMatchStats({ correct: 0, attempts: 0 })
    setMatchComplete(false)
  }, [flashcards])

  const handleTileClick = (side, pairId) => {
    if (matchedIds.has(pairId) || wrongPair) return
    if (side === 'left') setSelLeft(prev => prev === pairId ? null : pairId)
    else setSelRight(prev => prev === pairId ? null : pairId)
  }

  // ── Memory game ─────────────────────────────────────────────────────────────
  const startMemoryRound = useCallback(() => {
    const validCards = flashcards.filter(c => c.translations?.[0]?.target)
    if (validCards.length === 0) return
    const pool = [...validCards].sort(() => Math.random() - 0.5)
    const picked = pool.slice(0, Math.min(6, pool.length))
    const tiles = []
    picked.forEach((card, i) => {
      tiles.push({
        id: `s-${i}`,
        pairId: i,
        side: 'source',
        label: card.source,
        lang: card.sourceLanguage,
        targetId: card.translations[0].targetId,
      })
      tiles.push({
        id: `t-${i}`,
        pairId: i,
        side: 'target',
        label: card.translations[0].target,
        lang: card.translations[0].targetLanguage,
        targetId: card.translations[0].targetId,
      })
    })
    setMemoryTiles([...tiles].sort(() => Math.random() - 0.5))
    setMemoryFlipped([])
    setMemoryMatched(new Set())
    setMemoryStats({ correct: 0, attempts: 0 })
    setMemoryComplete(false)
  }, [flashcards])

  const handleMemoryClick = (tileId) => {
    if (memoryFlipped.length >= 2) return
    if (memoryFlipped.includes(tileId)) return
    const tile = memoryTiles.find(t => t.id === tileId)
    if (!tile || memoryMatched.has(tile.pairId)) return
    // Read the target-language side aloud (only the foreign one, never English).
    if (memorySpeakForeign && tile.lang && tile.lang !== 'eng' && tile.label) {
      speak(tile.label, voices[tile.lang] || voices['eng'], voiceSpeed)
    }
    setMemoryFlipped(prev => [...prev, tileId])
  }

  // Resolve a flipped pair (match or mismatch) without depending on stale closures
  useEffect(() => {
    if (memoryFlipped.length !== 2) return
    const [aId, bId] = memoryFlipped
    const a = memoryTiles.find(t => t.id === aId)
    const b = memoryTiles.find(t => t.id === bId)
    if (!a || !b) return
    setMemoryStats(prev => ({ ...prev, attempts: prev.attempts + 1 }))
    if (a.pairId === b.pairId && a.side !== b.side) {
      setMemoryMatched(prev => new Set([...prev, a.pairId]))
      setMemoryStats(prev => ({ ...prev, correct: prev.correct + 1 }))
      recordPractice(a.targetId, true)
      setTimeout(() => setMemoryFlipped([]), 450)
    } else {
      recordPractice(a.targetId, false)
      setTimeout(() => setMemoryFlipped([]), 900)
    }
  }, [memoryFlipped, memoryTiles])

  useEffect(() => {
    if (memoryTiles.length > 0 && memoryMatched.size === memoryTiles.length / 2) {
      setMemoryComplete(true)
      celebrate(window.innerWidth / 2, window.innerHeight / 2.5, { count: 40, spread: 220 })
    }
  }, [memoryMatched, memoryTiles.length])

  // ── Sentence builder ────────────────────────────────────────────────────────
  const startBuildForCurrent = useCallback(() => {
    const card = flashcards[currentIndex]
    if (!card?.translations?.[0]?.target) return
    const target = card.translations[0].target
    const targetWords = target.split(/\s+/).filter(Boolean)

    // Pull distractor words from other cards' targets (same target lang preferred)
    const sameLang = card.translations[0].targetLanguage
    const distractorPool = []
    flashcards.forEach((c, i) => {
      if (i === currentIndex) return
      const t = c.translations?.[0]?.target
      const lang = c.translations?.[0]?.targetLanguage
      if (!t) return
      if (sameLang && lang && lang !== sameLang) return
      t.split(/\s+/).filter(Boolean).forEach(w => {
        if (!targetWords.includes(w) && !distractorPool.includes(w)) distractorPool.push(w)
      })
    })
    const numDistractors = Math.min(4, Math.max(2, Math.ceil(targetWords.length / 2)))
    const picks = [...distractorPool].sort(() => Math.random() - 0.5).slice(0, numDistractors)

    const bankWords = [...targetWords, ...picks].sort(() => Math.random() - 0.5)
    const bank = bankWords.map((w, i) => ({ id: i, word: w, used: false }))
    setBuildBank(bank)
    setBuildAnswer([])
    setBuildStatus(null)
  }, [flashcards, currentIndex])

  useEffect(() => {
    if (isBuildMode) startBuildForCurrent()
  }, [isBuildMode, currentIndex, startBuildForCurrent])

  const handleBuildBankClick = (wordId) => {
    if (buildStatus === 'correct') return
    const w = buildBank.find(x => x.id === wordId)
    if (!w || w.used) return
    setBuildBank(prev => prev.map(x => x.id === wordId ? { ...x, used: true } : x))
    setBuildAnswer(prev => [...prev, wordId])
    setBuildStatus(null)
  }

  const handleBuildAnswerClick = (idx) => {
    if (buildStatus === 'correct') return
    const wordId = buildAnswer[idx]
    setBuildBank(prev => prev.map(x => x.id === wordId ? { ...x, used: false } : x))
    setBuildAnswer(prev => prev.filter((_, i) => i !== idx))
    setBuildStatus(null)
  }

  const handleBuildCheck = (e) => {
    const card = flashcards[currentIndex]
    const target = card?.translations?.[0]?.target
    if (!target || buildAnswer.length === 0) return
    const answerStr = buildAnswer.map(id => buildBank.find(x => x.id === id)?.word).join(' ')
    const isCorrect = answerStr === target
    recordPractice(card?.translations?.[0]?.targetId, isCorrect)
    if (isCorrect) {
      if (e) celebrate(e.clientX, e.clientY)
      setBuildStatus('correct')
      if (autoSpeakCorrect) {
        // Read the foreign answer, never the English.
        const f = foreignOf(card)
        if (f) speak(f.text, voices[f.lang] || voices['eng'], voiceSpeed)
        else speak(target, voices[card.translations[0].targetLanguage] || voices['eng'], voiceSpeed)
      }
      setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % flashcards.length)
      }, 1800)
    } else {
      setBuildStatus('incorrect')
    }
  }

  if (loading && !providedFlashcards) return <div className={`${styles.flashcardView} ${styles[theme]}`}><div className={styles.status}>Loading flashcards...</div></div>
  if (error && !providedFlashcards) return <div className={`${styles.flashcardView} ${styles[theme]}`}><div className={styles.status}>Error loading cards: {error.message}</div></div>

  const handleAddSubmit = async (e) => {
    e.preventDefault()
    if (!sourceLang || !targetLang) return
    
    if (isBulkMode) {
      if (!bulkTsvText.trim()) return
      const lines = bulkTsvText.trim().split('\n')
      for (const line of lines) {
        const [sourceText, targetText] = line.split('\t')
        if (sourceText && targetText) {
          await addUserSentence({
            variables: {
              sourceText: sourceText.trim(),
              targetText: targetText.trim(),
              sourceLang,
              targetLang,
              tags: newTag ? [newTag] : [],
              username,
              sharedWith: []
            }
          })
        }
      }
      setIsAdding(false)
      setBulkTsvText('')
      refetch()
    } else {
      if (!newSourceText || !newTargetText) return
      addUserSentence({
        variables: {
          sourceText: newSourceText,
          targetText: newTargetText,
          sourceLang,
          targetLang,
          tags: newTag ? [newTag] : [],
          username,
          sharedWith: []
        }
      })
    }
  }

  const handleOpenAdd = () => {
    setNewTag(tagFilter)
    setIsAdding(true)
  }

  const currentCard = flashcards[currentIndex]

  return (
    <div className={`${styles.flashcardView} ${styles[theme]}`}>
      <div className={styles.gridBg} />
      {isWriteMode && (
        <HandwritingView cards={flashcards} voiceSpeed={voiceSpeed} onClose={() => setIsWriteMode(false)} />
      )}
      {isFlowMode && (
        <ReadingFlowView cards={flashcards} voiceSpeed={voiceSpeed} onClose={() => setIsFlowMode(false)} />
      )}
      {isRecallMode && (
        <RecallFlowView cards={flashcards} voiceSpeed={voiceSpeed} onClose={() => setIsRecallMode(false)} />
      )}
      {isRecallPlusMode && (
        <RecallPlusView cards={flashcards} voiceSpeed={voiceSpeed} onClose={() => setIsRecallPlusMode(false)} />
      )}
      {isChatMode && (
        <VocabChatView cards={flashcards} voiceSpeed={voiceSpeed} onClose={() => setIsChatMode(false)} />
      )}
      <button className={styles.themeBtn} onClick={toggleTheme} title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
        {theme === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
      </button>
      <button className={styles.closeBtn} onClick={() => {
        setIsVisible(false);
        if (onClose) onClose();
      }}>
        <CloseIcon />
      </button>

      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.titleGroup}>
            <div className={styles.label}>Mac Studio AI</div>
            <h1 className={styles.title}>
              Flashcard <span>Master</span>
            </h1>
            
            {!providedFlashcards && (
              <div className={styles.filters}>
              <select 
                className={styles.filterSelect} 
                value={sourceLang} 
                onChange={(e) => setSourceLang(e.target.value)}
              >
                <option value="">Any Source Lang</option>
                {Object.entries(languages).map(([code, langName]) => (
                  <option key={code} value={code}>{langName}</option>
                ))}
              </select>
              
              <select 
                className={styles.filterSelect} 
                value={targetLang} 
                onChange={(e) => setTargetLang(e.target.value)}
              >
                <option value="">Any Target Lang</option>
                {Object.entries(languages).map(([code, langName]) => (
                  <option key={code} value={code}>{langName}</option>
                ))}
              </select>

              <input 
                type="text" 
                className={styles.filterInput}
                placeholder="Filter by tag (Press Enter)..."
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setTagFilter(tagInput)
                  }
                }}
                onBlur={() => setTagFilter(tagInput)}
              />
            </div>
            )}
          </div>

          <div className={styles.stats}>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Streak</span>
              <span className={styles.statValue} ref={streakRef} style={{ display: 'inline-block' }}>
                {stats.streak}{stats.streak >= 3 ? ' 🔥' : ''}
              </span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Mastered</span>
              <span className={styles.statValue}>{stats.mastered}</span>
            </div>
          </div>
        </header>

        {isAdding ? (
          <form className={styles.addForm} onSubmit={handleAddSubmit}>
            <h2 className={styles.addTitle}>Add New Flashcard</h2>
            <div className={styles.addFields}>
              <div className={styles.addLangInfo}>
                Creating card for: {languages[sourceLang]} → {languages[targetLang]}
              </div>
              
              <div className={styles.bulkModeToggle}>
                <label>
                  <input 
                    type="checkbox" 
                    checked={isBulkMode} 
                    onChange={e => setIsBulkMode(e.target.checked)} 
                  /> Bulk Add (TSV Mode)
                </label>
              </div>

              {isBulkMode ? (
                <textarea 
                  className={styles.addInput} 
                  placeholder="Paste TSV here: SourceText [TAB] TargetText" 
                  value={bulkTsvText} 
                  onChange={e => setBulkTsvText(e.target.value)} 
                  rows={6}
                  autoFocus
                />
              ) : (
                <>
                  <textarea 
                    className={styles.addInput} 
                    placeholder={`Source Text (${languages[sourceLang]})`} 
                    value={newSourceText} 
                    onChange={e => setNewSourceText(e.target.value)} 
                    autoFocus
                  />
                  <textarea 
                    className={styles.addInput} 
                    placeholder={`Target Text (${languages[targetLang]})`} 
                    value={newTargetText} 
                    onChange={e => setNewTargetText(e.target.value)} 
                  />
                </>
              )}
              
              <input 
                type="text" 
                className={styles.addInput}
                placeholder="Tag (e.g. food, verbs)"
                value={newTag}
                onChange={e => setNewTag(e.target.value)}
              />
              
              <div className={styles.addActions}>
                <button type="button" className={styles.navBtn} onClick={() => setIsAdding(false)}>Cancel</button>
                <button type="submit" className={`${styles.navBtn} ${styles.primaryBtn}`}>Save Card</button>
              </div>
            </div>
          </form>
        ) : isMatchMode && flashcards.length > 0 ? (
          <div className={styles.matchGame}>
            {matchComplete ? (
              <div className={styles.matchComplete}>
                <div className={styles.matchCompleteIcon}>✓</div>
                <div className={styles.matchCompleteTitle}>Round Complete!</div>
                <div className={styles.matchCompleteSub}>
                  {matchStats.correct} / {matchStats.attempts} correct
                </div>
                <div className={styles.navigation}>
                  <button className={styles.navBtn} onClick={() => setIsMatchMode(false)}>← Cards</button>
                  <button className={`${styles.navBtn} ${styles.primaryBtn}`} onClick={startMatchRound}>Next Round →</button>
                </div>
              </div>
            ) : (
              <>
                <div className={styles.matchHeader}>
                  <div className={styles.matchTitle}>Match the pairs</div>
                  <div className={styles.matchProgressBar}>
                    <div className={styles.matchProgressFill} style={{ width: `${leftTiles.length ? (matchedIds.size / leftTiles.length) * 100 : 0}%` }} />
                  </div>
                  <div className={styles.matchProgressText}>{matchedIds.size} / {leftTiles.length}</div>
                </div>
                <div className={`${styles.matchColumns} ${isAudioMatch ? styles.matchColumnsAudio : ''}`} ref={matchColsRef}>
                  {isAudioMatch && (
                    <svg className={styles.matchLinks}>
                      {matchLinks.map(l => {
                        const dx = Math.max(24, (l.x2 - l.x1) * 0.5)
                        return <path key={l.pairId} pathLength={1} className={styles.matchLink}
                          d={`M ${l.x1} ${l.y1} C ${l.x1 + dx} ${l.y1}, ${l.x2 - dx} ${l.y2}, ${l.x2} ${l.y2}`} />
                      })}
                    </svg>
                  )}
                  <div className={styles.matchColumn}>
                    {leftTiles.map(tile => {
                      const isMatched = matchedIds.has(tile.pairId)
                      const isSelected = selLeft === tile.pairId
                      const isWrong = wrongPair?.left === tile.pairId
                      return (
                        <div
                          key={tile.pairId}
                          ref={el => { leftTileRefs.current[tile.pairId] = el }}
                          className={[
                            styles.matchTile,
                            isAudioMatch ? styles.matchTileAudio : '',
                            isMatched ? styles.matchTileMatched : '',
                            isSelected ? styles.matchTileSelected : '',
                            isWrong ? styles.matchTileWrong : ''
                          ].join(' ')}
                          onClick={() => {
                            if (isMatched || wrongPair) return
                            handleTileClick('left', tile.pairId)
                            if (isAudioMatch) speak(tile.source, voices[tile.sourceLanguage] || voices['eng'], slowSpeed)
                          }}
                        >
                          {isAudioMatch
                            ? <span className={styles.matchAudioBtn} style={{ pointerEvents: 'none' }}><VolumeUpIcon style={{ fontSize: 22 }} /></span>
                            : tile.source}
                        </div>
                      )
                    })}
                  </div>
                  <div className={styles.matchColumn}>
                    {rightTiles.map(tile => {
                      const isMatched = matchedIds.has(tile.pairId)
                      const isSelected = selRight === tile.pairId
                      const isWrong = wrongPair?.right === tile.pairId
                      return (
                        <div
                          key={tile.pairId}
                          ref={el => { rightTileRefs.current[tile.pairId] = el }}
                          className={[
                            styles.matchTile,
                            isMatched ? styles.matchTileMatched : '',
                            isSelected ? styles.matchTileSelected : '',
                            isWrong ? styles.matchTileWrong : ''
                          ].join(' ')}
                          onClick={() => handleTileClick('right', tile.pairId)}
                        >{tile.target}</div>
                      )
                    })}
                  </div>
                </div>
                <div className={styles.navigation}>
                  <button className={styles.navBtn} onClick={() => setIsMatchMode(false)}>← Cards</button>
                  <button className={styles.navBtn} onClick={startMatchRound}><RefreshIcon fontSize="small" /> New Round</button>
                </div>
              </>
            )}
          </div>
        ) : isMemoryMode && flashcards.length > 0 ? (
          <div className={styles.matchGame}>
            {memoryComplete ? (
              <div className={styles.matchComplete}>
                <div className={styles.matchCompleteIcon}>✓</div>
                <div className={styles.matchCompleteTitle}>Memory cleared!</div>
                <div className={styles.matchCompleteSub}>
                  {memoryStats.correct} pairs in {memoryStats.attempts} attempts
                </div>
                <div className={styles.navigation}>
                  <button className={styles.navBtn} onClick={() => setIsMemoryMode(false)}>← Cards</button>
                  <button className={`${styles.navBtn} ${styles.primaryBtn}`} onClick={startMemoryRound}>Next Round →</button>
                </div>
              </div>
            ) : (
              <>
                <div className={styles.matchHeader}>
                  <div className={styles.matchTitle}>Memory — flip two to find a pair</div>
                  <label className={styles.speakToggle} title="Read the target-language tile aloud when flipped (never the English)">
                    <input type="checkbox" checked={memorySpeakForeign} onChange={e => setMemorySpeakForeign(e.target.checked)} />
                    🔊 Say the {languages[memoryTiles.find(t => t.lang && t.lang !== 'eng')?.lang] || 'target'}
                  </label>
                  <div className={styles.matchProgressBar}>
                    <div
                      className={styles.matchProgressFill}
                      style={{ width: `${memoryTiles.length ? (memoryMatched.size / (memoryTiles.length / 2)) * 100 : 0}%` }}
                    />
                  </div>
                  <div className={styles.matchProgressText}>
                    {memoryMatched.size} / {memoryTiles.length / 2} · {memoryStats.attempts} flips
                  </div>
                </div>
                <div className={styles.memoryGrid}>
                  {memoryTiles.map(tile => {
                    const isMatched = memoryMatched.has(tile.pairId)
                    const isFaceUp = isMatched || memoryFlipped.includes(tile.id)
                    return (
                      <div
                        key={tile.id}
                        className={[
                          styles.memoryCard,
                          isFaceUp ? styles.memoryCardFaceUp : '',
                          isMatched ? styles.memoryCardMatched : '',
                        ].join(' ')}
                        onClick={() => handleMemoryClick(tile.id)}
                      >
                        {isFaceUp ? (
                          <span className={styles.memoryCardLabel}>{tile.label}</span>
                        ) : (
                          <span className={styles.memoryCardBack}>?</span>
                        )}
                      </div>
                    )
                  })}
                </div>
                <div className={styles.navigation}>
                  <button className={styles.navBtn} onClick={() => setIsMemoryMode(false)}>← Cards</button>
                  <button className={styles.navBtn} onClick={startMemoryRound}><RefreshIcon fontSize="small" /> New Round</button>
                </div>
              </>
            )}
          </div>
        ) : isBuildMode && flashcards.length > 0 ? (
          <div className={styles.scrambleScene} ref={sceneRef}>
            <div className={styles.scrambleHeader}>
              <div className={styles.matchTitle}>Build the sentence</div>
              <div className={styles.cardText} style={{ fontSize: '1.4rem' }}>{currentCard?.source}</div>
              <div className={styles.cardSubtext}>{currentCard?.sourceLanguage}</div>
            </div>

            <div className={[
              styles.buildAnswer,
              buildStatus === 'correct' ? styles.scrambleAnswerCorrect : '',
              buildStatus === 'incorrect' ? styles.scrambleAnswerWrong : '',
            ].join(' ')}>
              {buildAnswer.length === 0 ? (
                <span className={styles.scramblePlaceholder}>tap words below to build the translation…</span>
              ) : (
                buildAnswer.map((wordId, idx) => {
                  const w = buildBank.find(x => x.id === wordId)
                  return (
                    <button
                      key={`${wordId}-${idx}`}
                      className={styles.buildSlot}
                      onClick={() => handleBuildAnswerClick(idx)}
                    >
                      {w?.word}
                    </button>
                  )
                })
              )}
            </div>

            <div className={styles.buildBank}>
              {buildBank.map((w, idx) => (
                <button
                  key={`${w.id}-${idx}`}
                  className={[styles.buildTile, w.used ? styles.scrambleTileUsed : ''].join(' ')}
                  disabled={w.used}
                  onClick={() => handleBuildBankClick(w.id)}
                >
                  {w.word}
                </button>
              ))}
            </div>

            {buildStatus === 'correct' && (
              <div className={styles.scrambleReveal}>
                <div className={`${styles.cardText} ${styles.cardTextAccent}`}>{currentCard?.translations?.[0]?.target}</div>
                <Romanization text={currentCard?.translations?.[0]?.target} lang={currentCard?.translations?.[0]?.targetLanguage} />
              </div>
            )}

            <div className={styles.navigation}>
              <button className={styles.navBtn} onClick={handlePrev}>
                <NavigateBeforeIcon /> Prev
              </button>
              <button className={`${styles.navBtn} ${styles.primaryBtn}`} onClick={() => setIsBuildMode(false)}>
                Back to Cards
              </button>
              <button className={`${styles.navBtn} ${styles.primaryBtn}`} onClick={handleBuildCheck} disabled={buildAnswer.length === 0 || buildStatus === 'correct'}>
                Check
              </button>
              <button className={`${styles.navBtn} ${styles.primaryBtn}`} onClick={handleNext}>
                Skip <NavigateNextIcon />
              </button>
            </div>
          </div>
        ) : isQuizMode && flashcards.length > 0 ? (
          <div className={styles.quizScene}>
            <div className={styles.quizHeader}>
              <label className={styles.autoSpeakToggle}>
                <input 
                  type="checkbox" 
                  checked={autoSpeakCorrect} 
                  onChange={(e) => setAutoSpeakCorrect(e.target.checked)} 
                />
                Auto-speak on correct
              </label>
            </div>
            <div className={styles.quizQuestion} ref={sceneRef}>
              {quizType === 'reading' ? (
                <>
                  <div className={styles.cardText}>{currentCard.source}</div>
                  <div className={styles.cardSubtext}>{currentCard.sourceLanguage}</div>
                  {quizAnswered === 'correct' && (
                    <div style={{ marginTop: '1.5rem', animation: 'slideIn 0.3s ease-out' }}>
                      <div className={`${styles.cardText} ${styles.cardTextAccent}`}>{currentCard.translations?.[0]?.target}</div>
                      <Romanization text={currentCard.translations?.[0]?.target} lang={currentCard.translations?.[0]?.targetLanguage} />
                    </div>
                  )}
                  <div className={styles.cardControls}>
                    <button 
                      className={styles.controlBtn} 
                      onClick={(e) => handleSpeak(e, currentCard.source, currentCard.sourceLanguage)}
                    >
                      <VolumeUpIcon fontSize="small" />
                    </button>
                  </div>
                </>
              ) : (
                <div className={styles.listeningPrompt}>
                  <button 
                    className={styles.giantPlayBtn} 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSpeak(e, currentCard.source, currentCard.sourceLanguage)
                    }}
                  >
                    <VolumeUpIcon style={{ fontSize: 60 }} />
                  </button>
                  <div className={styles.cardSubtext}>Listen and select the matching word</div>
                  {quizAnswered === 'correct' && (
                    <div style={{ marginTop: '1.5rem', animation: 'slideIn 0.3s ease-out' }}>
                      <div className={`${styles.cardText} ${styles.cardTextAccent}`}>{currentCard.translations?.[0]?.target}</div>
                      <Romanization text={currentCard.translations?.[0]?.target} lang={currentCard.translations?.[0]?.targetLanguage} />
                      <div className={styles.cardSubtext} style={{ marginTop: '0.5rem' }}>{currentCard.source}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className={styles.quizOptions}>
              {isQuizLoading ? (
                <div className={styles.status}>Generating options via Ollama...</div>
              ) : (
                quizOptions.map((opt, i) => {
                  const isCorrectOpt = opt === currentCard.translations?.[0]?.target;
                  let btnStateClass = '';
                  if (selectedOption === opt) {
                    btnStateClass = isCorrectOpt ? styles.correctBtn : styles.incorrectBtn;
                  } else if (quizAnswered === 'correct' && isCorrectOpt) {
                    btnStateClass = styles.correctBtn;
                  }
                  
                  return (
                    <div 
                      key={i} 
                      className={`${styles.quizOptionWrapper} ${btnStateClass}`}
                    >
                      <div 
                        className={styles.quizOptionContent}
                        onClick={(e) => handleQuizAnswer(opt, e)}
                      >
                        {opt}
                      </div>
                      <button 
                        className={styles.optionSpeakBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSpeak(e, opt, currentCard.translations?.[0]?.targetLanguage || targetLang)
                        }}
                      >
                        <VolumeUpIcon fontSize="small" />
                      </button>
                    </div>
                  )
                })
              )}
            </div>
            <div className={styles.navigation}>
              <button className={styles.navBtn} onClick={handlePrev}>
                <NavigateBeforeIcon /> Prev
              </button>
              <button className={`${styles.navBtn} ${styles.primaryBtn}`} onClick={() => setIsQuizMode(false)}>
                Back to Cards
              </button>
              <button className={`${styles.navBtn} ${styles.primaryBtn}`} onClick={handleNext}>
                Skip <NavigateNextIcon />
              </button>
            </div>
          </div>
        ) : flashcards.length > 0 ? (
          <>
            <div className={styles.cardModeToggles}>
              <label className={styles.autoSpeakToggle}>
                <input
                  type="checkbox"
                  checked={showRomanization}
                  onChange={(e) => setShowRomanization(e.target.checked)}
                />
                Show romanization
              </label>
            </div>

            <div className={`${styles.cardScene} ${currentCard?.sourceImageUrl ? styles.cardSceneTall : ''}`} onClick={handleFlip} ref={sceneRef}>
              <div className={`${styles.card} ${isFlipped ? styles.isFlipped : ''}`}>
                <div className={styles.cardFace}>
                  {!isFlipped && currentCard.source && (
                    <MnemonicImage
                      text={currentCard.source}
                      lang={currentCard.sourceLanguage}
                      meaning={currentCard.translations?.[0]?.target}
                      imageUrl={currentCard.sourceImageUrl}
                      imageDescription={currentCard.sourceImageDescription}
                      username={username}
                      onSaved={(url, desc) => {
                        // Optimistic local update so the new image shows up
                        // immediately without a full refetch round-trip.
                        setFlashcards(prev => prev.map((c, i) =>
                          i === currentIndex
                            ? { ...c, sourceImageUrl: url, sourceImageDescription: desc }
                            : c
                        ))
                      }}
                    />
                  )}
                  <div className={styles.cardText}>{currentCard.source}</div>
                  {showRomanization && !isFlipped && currentCard.source && (
                    <Romanization text={currentCard.source} lang={currentCard.sourceLanguage} />
                  )}
                  <div className={styles.cardSubtext}>{currentCard.sourceLanguage}</div>
                  <div className={styles.cardControls}>
                    <button
                      className={styles.controlBtn}
                      onClick={(e) => handleSpeak(e, currentCard.source, currentCard.sourceLanguage)}
                    >
                      <VolumeUpIcon fontSize="small" />
                    </button>
                  </div>
                </div>

                <div className={`${styles.cardFace} ${styles.cardBack}`}>
                  <div className={styles.cardText}>
                    {currentCard.translations?.[0]?.target || 'No translation'}
                  </div>
                  {showRomanization && isFlipped && currentCard.translations?.[0]?.target && (
                    <Romanization text={currentCard.translations[0].target} lang={currentCard.translations[0].targetLanguage} />
                  )}
                  <div className={styles.cardSubtext}>
                    {currentCard.translations?.[0]?.targetLanguage || ''}
                  </div>
                  <div className={styles.cardControls}>
                    <button 
                      className={styles.controlBtn} 
                      onClick={(e) => handleSpeak(e, currentCard.translations?.[0]?.target, currentCard.translations?.[0]?.targetLanguage)}
                    >
                      <VolumeUpIcon fontSize="small" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.navigation}>
              <button className={styles.navBtn} onClick={handlePrev}>
                <NavigateBeforeIcon /> Prev
              </button>
              <button className={styles.navBtn} onClick={handleNext}>
                Next <NavigateNextIcon />
              </button>
            </div>

            <div className={styles.gameModeGrid}>
              <button className={`${styles.navBtn} ${styles.primaryBtn}`} onClick={() => startMode('match')}>Match</button>
              <button className={`${styles.navBtn} ${styles.primaryBtn}`} onClick={() => startMode('audio-match')}>Audio Match</button>
              <button className={`${styles.navBtn} ${styles.primaryBtn}`} onClick={() => startMode('memory')}>Memory</button>
              <button className={`${styles.navBtn} ${styles.primaryBtn}`} onClick={() => startMode('build')}>Build</button>
              <button className={`${styles.navBtn} ${styles.primaryBtn}`} onClick={() => startMode('reading')}>Read Quiz</button>
              <button className={`${styles.navBtn} ${styles.primaryBtn}`} onClick={() => startMode('listening')}>Listen Quiz</button>
              <button className={`${styles.navBtn} ${styles.primaryBtn}`} onClick={() => startMode('write')}>✍️ Write</button>
              <button className={`${styles.navBtn} ${styles.primaryBtn}`} onClick={() => startMode('flow')}>🎤 Flow</button>
              <button className={`${styles.navBtn} ${styles.primaryBtn}`} onClick={() => startMode('recall')}>🧠 Recall</button>
              <button className={`${styles.navBtn} ${styles.primaryBtn}`} onClick={() => startMode('recallplus')}>⚡ Recall+</button>
              {/* Verb Drill now lives in Special Mode. Chat is here provisionally — see the note by isChatMode. */}
              <button className={`${styles.navBtn} ${styles.primaryBtn}`} onClick={() => startMode('chat')}>💬 Chat</button>
            </div>

            {!providedFlashcards && (
              <div className={styles.deckActions}>
                <button className={styles.navBtn} onClick={handleOpenAdd}>+ Add Card</button>
                <button className={styles.navBtn} onClick={() => refetch()}>
                  <RefreshIcon fontSize="small" /> Refresh
                </button>
              </div>
            )}
          </>
        ) : (
          <div className={styles.emptyState}>
            <p>No sentences found.</p>
            {!providedFlashcards && (
              <button className={`${styles.navBtn} ${styles.primaryBtn}`} onClick={handleOpenAdd}>
                + Add First Card
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
